import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, Save, X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

const TradeIdeasEdit = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const logoSrc = '/logo.png';

  const [formData, setFormData] = useState({
    date: '',
    idea_number: 1,
    title: '',
    content: '',
  });

  const [existingImages, setExistingImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEntry, setFetchingEntry] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Admin email - only this user can edit trade ideas
  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'your-email@example.com';
  const isAdmin = user && user.email === ADMIN_EMAIL;

  // Fetch entry data
  useEffect(() => {
    if (!isAdmin) return;
    const fetchEntry = async () => {
      try {
        const { data, error } = await supabase
          .from('trade_ideas')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;

        if (data) {
          setFormData({
            date: data.date,
            idea_number: data.idea_number,
            title: data.title,
            content: data.content,
          });
          setExistingImages(data.image_urls || []);
        }
      } catch (err) {
        console.error('Error fetching entry:', err);
        setError('Failed to load entry. It may not exist.');
      } finally {
        setFetchingEntry(false);
      }
    };

    fetchEntry();
  }, [id, isAdmin]);

  // Access control check
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#111315] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-neutral-400 mb-6">
            {!user ? 'You must be logged in to access this page.' : 'Only administrators can edit trade ideas.'}
          </p>
          <Link
            to={!user ? "/login" : "/trade-ideas"}
            className="px-6 py-3 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition"
          >
            {!user ? 'Go to Login' : 'Back to Trade Ideas'}
          </Link>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'idea_number' ? parseInt(value) : value
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setImageFiles(prev => [...prev, ...files]);

      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, { file: file.name, preview: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeNewImage = (index) => {
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    if (imageFiles.length === 0) return [];

    try {
      const uploadPromises = imageFiles.map(async (file) => {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = fileName;

        const { error } = await supabase.storage
          .from('blog-images')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (error) {
          console.error('Upload error:', error);
          throw new Error(`Failed to upload ${file.name}: ${error.message}`);
        }

        const { data: urlData } = supabase.storage
          .from('blog-images')
          .getPublicUrl(filePath);

        return urlData.publicUrl;
      });

      return await Promise.all(uploadPromises);
    } catch (err) {
      console.error('uploadImages error:', err);
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // Upload new images if any
      const newImageUrls = await uploadImages();

      // Combine existing images with new ones
      const allImageUrls = [...existingImages, ...newImageUrls];

      // Update blog entry
      const { error: updateError } = await supabase
        .from('trade_ideas')
        .update({
          date: formData.date,
          idea_number: formData.idea_number,
          title: formData.title,
          content: formData.content,
          image_urls: allImageUrls,
        })
        .eq('id', id);

      if (updateError) throw updateError;

      setSuccess('Trade idea updated successfully!');

      // Redirect after a short delay
      setTimeout(() => {
        navigate('/trade-ideas');
      }, 1500);
    } catch (err) {
      console.error('Error updating entry:', err);
      setError(err.message || 'Failed to update entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchingEntry) {
    return (
      <div className="min-h-screen bg-[#111315] text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#CBB26A] mx-auto mb-4"></div>
          <p className="text-neutral-400">Loading entry...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111315] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#111315]/95 backdrop-blur-sm border-b border-neutral-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <img
                src={logoSrc}
                alt="HDAlgos"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <span className="text-2xl font-black tracking-tight">
                <span className="text-white">HD</span>
                <span className="text-[#CBB26A]">Algos</span>
              </span>
            </Link>

            <Link
              to="/trade-ideas"
              className="flex items-center gap-2 text-neutral-400 hover:text-white transition"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Trade Ideas</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="pt-20 px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl font-black mb-8">
            <span className="text-[#CBB26A]">Edit</span>
            <span className="text-white"> Analysis Example</span>
          </h1>

          {error && (
            <div className="mb-6 p-4 bg-red-950 border border-red-900 rounded-lg">
              <p className="text-red-300">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-950 border border-green-900 rounded-lg">
              <p className="text-green-300">{success}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Date and Example Number */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-bold mb-2 text-neutral-300">
                  Date
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg focus:outline-none focus:border-[#CBB26A] text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2 text-neutral-300">
                  Example Number
                </label>
                <input
                  type="number"
                  name="idea_number"
                  value={formData.idea_number}
                  onChange={handleInputChange}
                  min="1"
                  required
                  className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg focus:outline-none focus:border-[#CBB26A] text-white"
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-bold mb-2 text-neutral-300">
                Title
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                placeholder="e.g., SPY Long Entry"
                className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg focus:outline-none focus:border-[#CBB26A] text-white"
              />
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-bold mb-2 text-neutral-300">
                Content
              </label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows="10"
                placeholder="Describe the trade setup, entry, exit, etc..."
                className="w-full px-4 py-3 bg-neutral-900 border border-neutral-700 rounded-lg focus:outline-none focus:border-[#CBB26A] text-white resize-none"
              />
            </div>

            {/* Existing Images */}
            {existingImages.length > 0 && (
              <div>
                <label className="block text-sm font-bold mb-2 text-neutral-300">
                  Current Images
                </label>
                <div className="grid grid-cols-2 gap-4">
                  {existingImages.map((url, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={url}
                        alt={`Existing ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-neutral-700"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-red-900 hover:bg-red-800 rounded-lg opacity-0 group-hover:opacity-100 transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-bold mb-2 text-neutral-300">
                Add New Images (Optional)
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-3 bg-neutral-800 hover:bg-neutral-700 rounded-lg cursor-pointer transition">
                  <Upload className="w-5 h-5" />
                  <span>Choose Images</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
                <span className="text-sm text-neutral-500">
                  {imageFiles.length > 0 ? `${imageFiles.length} new image(s) selected` : 'No new images selected'}
                </span>
              </div>

              {/* New Image Previews */}
              {imagePreviews.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={preview.preview}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-neutral-700"
                      />
                      <button
                        type="button"
                        onClick={() => removeNewImage(index)}
                        className="absolute top-2 right-2 p-1.5 bg-red-900 hover:bg-red-800 rounded-lg opacity-0 group-hover:opacity-100 transition"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <div className="absolute bottom-2 left-2 px-2 py-1 bg-black/70 rounded text-xs">
                        {preview.file}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-[#CBB26A] text-black font-bold rounded-lg hover:bg-[#A8914D] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-5 h-5" />
                <span>{loading ? 'Saving...' : 'Save Changes'}</span>
              </button>

              <Link
                to="/trade-ideas"
                className="px-6 py-4 bg-neutral-800 hover:bg-neutral-700 text-white font-bold rounded-lg transition"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TradeIdeasEdit;
